import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ShareButton from './ShareButton';
import { trackExternalLink } from '../utils/analytics';

const { FiBell, FiClock, FiAlertTriangle, FiInfo, FiCheckCircle, FiRss, FiExternalLink, FiNavigation } = FiIcons;

const AlertFeed = ({ alerts = [], loading = false }) => {
  const [filter, setFilter] = useState('all'); // all, critical, advisory, system

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.type === filter;
  });

  const getAlertStyles = (type) => {
    switch (type) {
      case 'critical':
        return {
          bg: 'bg-red-50 border-red-200',
          icon: FiAlertTriangle,
          iconColor: 'text-red-600',
          badge: 'bg-red-600 text-white',
          label: 'CRITICAL'
        };
      case 'advisory':
        return {
          bg: 'bg-orange-50 border-orange-200',
          icon: FiNavigation,
          iconColor: 'text-orange-600',
          badge: 'bg-orange-500 text-white',
          label: 'ADVISORY'
        };
      case 'system':
        return {
          bg: 'bg-blue-50 border-blue-200',
          icon: FiRss,
          iconColor: 'text-blue-600',
          badge: 'bg-blue-600 text-white',
          label: 'SYSTEM'
        };
      default:
        return {
          bg: 'bg-gray-50 border-gray-200',
          icon: FiInfo,
          iconColor: 'text-gray-600',
          badge: 'bg-gray-600 text-white',
          label: 'INFO'
        };
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden flex flex-col h-[600px]"
    >
      {/* Feed Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <SafeIcon icon={FiBell} className="text-xl animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Live Situation Feed</h2>
              <p className="text-xs text-blue-100">Real-time Iligan & Mindanao Updates</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="flex h-2 w-2 rounded-full bg-green-400"></span>
            <span className="text-[10px] font-bold tracking-widest uppercase">Live</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 p-1 bg-black/10 rounded-xl">
          {['all', 'critical', 'advisory', 'system'].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg transition-all uppercase tracking-wider ${
                filter === t ? 'bg-white text-blue-700 shadow-sm' : 'text-white/70 hover:bg-white/5'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Feed Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4 opacity-50">
            <SafeIcon icon={FiRss} className="text-4xl animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-500">Syncing with emergency servers...</p>
          </div>
        ) : filteredAlerts.length > 0 ? (
          <AnimatePresence mode="popLayout">
            {filteredAlerts.map((alert, index) => {
              const styles = getAlertStyles(alert.type);
              return (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className={`relative p-4 rounded-xl border-l-4 ${styles.bg} hover:shadow-md transition-shadow group`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded ${styles.badge}`}>
                        {styles.label}
                      </span>
                      <span className="text-[10px] font-bold text-gray-400 flex items-center">
                        <SafeIcon icon={FiClock} className="mr-1" /> {alert.time}
                      </span>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                       <ShareButton size="small" className="!bg-transparent !text-gray-400 hover:!text-blue-600" />
                    </div>
                  </div>

                  <h3 className="font-bold text-gray-900 text-sm mb-1 leading-snug">
                    {alert.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed mb-3">
                    {alert.content}
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-black/5">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                         <span className="text-[8px] font-bold text-gray-500">
                           {alert.source === 'PAGASA' ? 'P' : 'D'}
                         </span>
                      </div>
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                        Source: {alert.source}
                      </span>
                    </div>
                    {alert.link && (
                      <a 
                        href={alert.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center"
                        onClick={() => trackExternalLink(alert.link, alert.title, 'Alert Feed')}
                      >
                        DETAILS <SafeIcon icon={FiExternalLink} className="ml-1" />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <SafeIcon icon={FiCheckCircle} className="text-3xl text-gray-400" />
            </div>
            <h3 className="font-bold text-gray-800">Clear Skies</h3>
            <p className="text-xs text-gray-500 max-w-[200px] mt-2">
              No active alerts matching your current filter. Check back shortly.
            </p>
          </div>
        )}
      </div>

      {/* Footer Attribution */}
      <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
        <p className="text-[9px] text-gray-400 font-medium uppercase tracking-widest">
          Verified by Iligan Weather Monitoring Network
        </p>
      </div>
    </motion.div>
  );
};

export default AlertFeed;